import { QueryClient, MutationCache, QueryCache } from "@tanstack/react-query"
import { useErrorToastStore } from "@/shared/stores/errorToastStore"
import { extractErrorMessage, getErrorStatusCode } from "@/shared/lib/errorMessages"

// Type for axios error with config metadata
interface ErrorWithConfig {
  config?: {
    metadata?: {
      handledByAxiosInterceptor?: boolean
    }
  }
}

// Helper to check if error was already handled by axios interceptor
function isAlreadyHandled(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('config' in error)) {
    return false
  }
  return !!(error as ErrorWithConfig).config?.metadata?.handledByAxiosInterceptor
}

// Error handler function for both queries and mutations
function handleError(error: unknown) {
  // Only toast if axios didn't already handle it
  if (!isAlreadyHandled(error)) {
    const statusCode = getErrorStatusCode(error);
    // Only show for 4xx and 5xx errors
    if (statusCode && statusCode >= 400) {
      const message = extractErrorMessage(error);
      const { addToast } = useErrorToastStore.getState();
      addToast(statusCode, message);

      // Log errors in development mode
      if (import.meta.env.DEV) {
        console.error('[React Query Error]', {
          statusCode,
          message,
          error,
        });
      }
    }
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleError,
  }),
  mutationCache: new MutationCache({
    onError: handleError,
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

// Query keys factory
export const queryKeys = {
  auth: {
    profile: () => ["auth", "profile"] as const,
  },
  farms: {
    all: () => ["farms"] as const,
    detail: (id: string) => ["farms", id] as const,
    members: (id: string) => ["farms", id, "members"] as const,
  },
  ponds: {
    all: (farmId: string) => ["ponds", farmId] as const,
    detail: (farmId: string, pondId: string) => ["ponds", farmId, pondId] as const,
  },
  waterQuality: {
    all: (pondId: string) => ["water-quality", pondId] as const,
    detail: (farmId: string, pondId: string, testId: string) =>
      ["water-quality", farmId, pondId, testId] as const,
  },
  fishHealth: {
    all: (pondId: string) => ["fish-health", pondId] as const,
  },
  maintenance: {
    all: (pondId: string) => ["maintenance", pondId] as const,
  },
  feed: {
    all: (pondId: string) => ["feed", pondId] as const,
  },
  team: {
    all: (farmId: string) => ["team", farmId] as const,
    detail: (farmId: string, farmUserId: string) => ["team", farmId, farmUserId] as const,
  },
  images: {
    signedUrls: (imageIds: string[]) => ["images", "signed-urls", ...imageIds.sort()] as const,
  },
  ai: {
    analyses: (farmId: string, params?: Record<string, unknown>) =>
      ["ai", "analyses", farmId, params] as const,
    analysis: (analysisId: string) => ["ai", "analysis", analysisId] as const,
    rateLimit: () => ["ai", "rate-limit"] as const,
    fishSpecies: (activeOnly: boolean) => ["ai", "fish-species", activeOnly] as const,
  },
  morningSummary: {
    farm: (farmId: string) => ["morning-summary", farmId] as const,
  },
}
